/**
 * Offboard or withdraw the register's selection (R6, W1): one reviewed Task
 * over the selected people. The server's preview says, person by person, what
 * each command would end and who it must leave out; the commit sends only the
 * people it can take, each on the version and fingerprint reviewed, and the
 * server takes all of them or nobody.
 */
import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { useTabDraft } from "@/components/ui/box-draft";
import type { Blocker, Box, Read, TaskBox } from "@/components/ui/box.schema";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ApiError } from "@/lib/api/client";
import { checkCommit } from "@/lib/api/commitSeams";
import { PEOPLE_QUERY_KEY, peopleApi, type PeopleLifecycleReview, type PeopleRegisterRow, type PeopleSelectionFailure } from "@/lib/api/people";

import { PersonVisitButton } from "../visit/person-management-actions";
import { resultState } from "../visit/access-boxes";
import { imam, type PendingCommit, refusalSentence, useTaskCommit } from "../visit/commit";
import type { Selected } from "./register-boxes";

export const LIFECYCLE_BOX = "people-bulk-lifecycle";
/** The server's bound on one offboard or withdraw selection. */
export const MAX_LIFECYCLE_SELECTION = 200;

type Action = "offboard" | "withdraw";
interface Draft { action: Action; note: string }

const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`;
const people = (count: number) => plural(count, "person", "people");
const students = (count: number) => plural(count, "student", "students");

/** What offboarding this one person ends, in a few words. */
function offboardSummary(review: PeopleLifecycleReview): string {
  const { effects } = review.offboard;
  const words = [
    ...(effects.enrollments ? [plural(effects.enrollments, "course seat", "course seats")] : []),
    ...(effects.teaching_assignments ? [plural(effects.teaching_assignments, "teaching assignment", "teaching assignments")] : []),
    ...(effects.invitations ? [plural(effects.invitations, "invitation", "invitations")] : []),
    ...(effects.student_withdrawn ? ["standing withdrawn"] : []),
    ...(effects.access_suspended ? ["sign-in stops"] : []),
  ];
  return words.length ? words.join(" · ") : review.archived_at ? "nothing current" : "archived only";
}

const withdrawSummary = (review: PeopleLifecycleReview) =>
  review.withdraw?.enrollments ? plural(review.withdraw.enrollments, "current course seat", "current course seats") : "no current course seats";

/** The refused selection's sentence, naming each person the server stopped on. */
function refusedSentence(error: unknown, fallback: string, nameOf: (id: string) => string): string {
  const failures = error instanceof ApiError && error.detail && typeof error.detail === "object"
    ? (error.detail as { failures?: PeopleSelectionFailure[] }).failures
    : undefined;
  const sentence = refusalSentence(error, fallback);
  if (!Array.isArray(failures) || !failures.length) return sentence;
  return [sentence, ...failures.map((failure) => `${nameOf(failure.id)}: ${failure.sentence}`)].join(" ");
}

export function useBulkLifecycle(selection: Map<string, Selected>, setSelection: (next: Map<string, Selected>) => void, shown: readonly PeopleRegisterRow[]): Box {
  const client = useQueryClient();
  const draft = useTabDraft<Draft, PendingCommit>({ box: LIFECYCLE_BOX, record: "selection", version: null, initial: { action: "offboard", note: "" } });
  const selected = [...selection.values()];
  const ids = selected.map((person) => person.key).sort();
  const tooMany = ids.length > MAX_LIFECYCLE_SELECTION;
  const preview = useQuery({
    queryKey: [...PEOPLE_QUERY_KEY, "lifecycle-preview", ids],
    queryFn: () => peopleApi.lifecyclePreview(ids),
    enabled: ids.length > 0 && !tooMany,
    retry: false,
  });
  const reviews = preview.data?.people ?? [];
  const nameOf = (personId: string) => selection.get(personId)?.name ?? personId;
  const byStudent = new Map(reviews.flatMap((review) => (review.withdraw ? [[review.withdraw.student_id, review.person_id] as const] : [])));
  const failedName = (id: string) => nameOf(byStudent.get(id) ?? id);

  const action = draft.value.action;
  const note = draft.value.note.trim();
  const offboarding = action === "offboard";
  const takes = (review: PeopleLifecycleReview) => (offboarding ? review.offboard.state : review.withdraw?.state) === "ready";
  const included = reviews.filter(takes);
  const leftOut = reviews.filter((review) => !takes(review));
  const hidden = selected.filter((person) => !shown.some((row) => row.person_id === person.key)).length;
  const leftOutReason = (review: PeopleLifecycleReview) =>
    offboarding ? review.offboard.reasons.join(" ") : review.withdraw ? review.withdraw.reasons.join(" ") : "No student record.";

  const confirmedLines = (answer: { offboarded?: number; withdrawn?: number; affected?: { enrollments: number; teaching_assignments: number; invitations: number }; access_suspended?: number }) =>
    answer.withdrawn !== undefined
      ? [`Withdrew ${students(answer.withdrawn)}`, "Current course seats closed and pending student invitations revoked", "Sign-in, teaching and completed work kept as they are"]
      : [
          `Offboarded ${people(answer.offboarded ?? 0)}`,
          ...(answer.affected?.enrollments ? [`Withdrew ${plural(answer.affected.enrollments, "course seat", "course seats")}`] : []),
          ...(answer.affected?.teaching_assignments ? [`Ended ${plural(answer.affected.teaching_assignments, "teaching assignment", "teaching assignments")}`] : []),
          ...(answer.affected?.invitations ? [`Revoked ${plural(answer.affected.invitations, "invitation", "invitations")}`] : []),
          ...(answer.access_suspended ? [`Stopped LMS sign-in for ${people(answer.access_suspended)}`] : []),
          "Archived. Completed work and closed terms kept as they are",
        ];
  const commit = useTaskCommit({
    subject: "people:bulk-lifecycle", keep: draft,
    words: {
      refused: offboarding ? "Nobody was offboarded." : "Nobody was withdrawn.",
      unknown: offboarding ? "Offboarding needs confirmation" : "Withdrawal needs confirmation",
      unknownSentence: "We could not confirm this change. Check its saved result before trying again.",
      checkNote: "Reads the saved result. Nothing is changed again.",
    },
    refresh: () => Promise.all([
      client.invalidateQueries({ queryKey: PEOPLE_QUERY_KEY }),
      client.invalidateQueries({ queryKey: ["invitations"] }),
      client.invalidateQueries({ queryKey: ["dashboard", "admin"] }),
    ]),
    checkBy: async (key) => {
      const checked = await checkCommit(key);
      if (checked.state !== "confirmed") return checked.state === "not_committed" ? "not_committed" : "unknown";
      setSelection(new Map());
      return { state: "confirmed", at: new Date().toISOString(), effects: confirmedLines(checked.receipt.result as Parameters<typeof confirmedLines>[0]), remains: [] };
    },
  });

  const effects = offboarding
    ? [
        `Offboard ${people(included.length)}: end their current course seats, teaching and pending invitations, and stop their LMS sign-in`,
        "Archive them. Completed work, closed terms and Google accounts stay as they are",
        ...(leftOut.length ? [`Leave out ${people(leftOut.length)}`] : []),
      ]
    : [
        `Withdraw ${students(included.length)}: close their current course seats and revoke pending student invitations`,
        "Keep their sign-in, teaching and completed work as they are",
        ...(leftOut.length ? [`Leave out ${people(leftOut.length)}`] : []),
      ];

  // A new selection after a confirmed change starts new work.
  const selectionMoved = selected.length > 0 && commit.result.state === "confirmed";
  useEffect(() => {
    if (selectionMoved) { commit.reset(); draft.set((value) => ({ ...value, note: "" })); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionMoved]);

  const choose = (next: Action) => { commit.reset(); draft.set((value) => ({ ...value, action: next })); };
  const list = (rows: PeopleLifecycleReview[], line: (review: PeopleLifecycleReview) => string) => (
    <ul className="grid gap-1 text-ui">
      {rows.map((review) => (
        <li key={review.person_id} className="[overflow-wrap:anywhere]">
          {nameOf(review.person_id)}<span className="text-muted-foreground"> · {line(review)}</span>
        </li>
      ))}
    </ul>
  );
  // The box stays drawn while its review reads, so a settled result is never hidden behind a loading read.
  const reviewRead: Read<null> = preview.isError && !preview.data
    ? { status: "failed", sentence: "We could not read what this would change.", retry: () => void preview.refetch() }
    : { status: "ready", value: null, readAt: new Date(preview.dataUpdatedAt || 0).toISOString() };
  const reading = !tooMany && ids.length > 0 && !preview.data;

  return {
    id: LIFECYCLE_BOX, kind: "task", label: "Offboard or withdraw selected",
    job: "End the selected people's current work together, or withdraw selected students.", owner: imam,
    fold: "choice", read: reviewRead,
    detail: () => `${selected.length} selected${hidden ? `, ${hidden} outside the current filter` : ""}`,
    state: () => resultState(commit.result, { class: "quiet", word: tooMany ? `Over ${MAX_LIFECYCLE_SELECTION} selected` : offboarding ? `${included.length} to offboard` : `${included.length} to withdraw` }),
    act: { does: "open", verb: "Review offboarding" }, candidate: { eligible: selected.length > 0, blocked: null, rank: null },
    parts: [
      {
        id: "lifecycle-change", label: "What to do", owner: imam, fold: "never", state: { class: "quiet", word: offboarding ? "Offboard" : "Withdraw" }, draft: "browser", keep: draft,
        render: () => (
          <fieldset className="grid gap-2">
            <legend className="mb-2 text-ui">Change for the selection</legend>
            <label className="flex min-h-11 items-start gap-2 text-ui"><input type="radio" className="mt-1" name="bulk-lifecycle" checked={offboarding} onChange={() => choose("offboard")} /><span>Offboard<span className="block text-meta text-muted-foreground">Ends current seats, teaching, invitations and LMS sign-in, then archives.</span></span></label>
            <label className="flex min-h-11 items-start gap-2 text-ui"><input type="radio" className="mt-1" name="bulk-lifecycle" checked={!offboarding} onChange={() => choose("withdraw")} /><span>Withdraw students<span className="block text-meta text-muted-foreground">Ends student standing and current seats only. Sign-in and teaching stay.</span></span></label>
          </fieldset>
        ),
      },
      {
        id: "lifecycle-who", label: "Who it changes", owner: imam, fold: "never", draft: "none",
        state: included.length ? { class: "draft", word: String(included.length) } : { class: "waiting", word: "0" },
        render: () => (
          <div className="grid gap-3">
            {tooMany ? <p className="text-ui">{`Choose at most ${MAX_LIFECYCLE_SELECTION} people at a time.`}</p> : reading ? <p className="text-ui text-muted-foreground" role="status">Reading what this would change…</p> : <>
              {included.length ? list(included, offboarding ? offboardSummary : withdrawSummary) : <p className="text-ui text-muted-foreground">{offboarding ? "Nobody selected can be offboarded together." : "Nobody selected can be withdrawn."}</p>}
              {leftOut.length > 0 && <div className="grid gap-2">
                <p className="text-ui">{`Left out (${leftOut.length})`}</p>
                <ul className="grid gap-2 text-ui">
                  {leftOut.map((review) => (
                    <li key={review.person_id} className="grid gap-1 [overflow-wrap:anywhere]">
                      <span>{nameOf(review.person_id)}<span className="text-muted-foreground"> · {leftOutReason(review)}</span></span>
                      {offboarding && review.offboard.state === "individual" && <div><PersonVisitButton personId={review.person_id} job="offboard" from={LIFECYCLE_BOX} label={`Offboard ${nameOf(review.person_id)}`} /></div>}
                    </li>
                  ))}
                </ul>
              </div>}
              <div><Button type="button" variant="outline" size="sm" onClick={() => { commit.reset(); void preview.refetch(); }}>Read again</Button></div>
            </>}
          </div>
        ),
      },
      {
        id: "lifecycle-reason", label: "Reason", owner: imam, fold: "never", draft: "browser", keep: draft,
        state: note ? { class: "done", word: "Given" } : { class: "waiting", word: "Needed" },
        render: () => (
          <div className="grid gap-1.5">
            <Label htmlFor="bulk-lifecycle-note">{offboarding ? "Why are they leaving?" : "Why are they withdrawing?"}</Label>
            <Textarea id="bulk-lifecycle-note" maxLength={1000} value={draft.value.note} onChange={(event) => { commit.reset(); draft.set((value) => ({ ...value, note: event.target.value })); }} />
            <p className="text-meta text-muted-foreground">Recorded on each person's history.</p>
          </div>
        ),
      },
    ],
    commit: {
      verb: offboarding ? `Offboard ${people(included.length)}` : `Withdraw ${students(included.length)}`,
      cap: "When you confirm", effects: () => effects.map((sentence, index) => ({ id: `effect-${index}`, sentence })),
      note: "Everyone listed changes together, or nobody does.",
      blockers: (): Blocker[] => [
        ...(tooMany ? [{ kind: "missing" as const, part: "lifecycle-who", sentence: `Choose at most ${MAX_LIFECYCLE_SELECTION} people at a time.` }] : []),
        ...(reading ? [{ kind: "missing" as const, part: "lifecycle-who", sentence: "Read what this would change first." }] : []),
        ...(preview.data && !included.length ? [{ kind: "missing" as const, part: "lifecycle-who", sentence: offboarding ? "Nobody selected can be offboarded together." : "Nobody selected can be withdrawn." }] : []),
        ...(!note ? [{ kind: "missing" as const, part: "lifecycle-reason", sentence: "Give a reason." }] : []),
      ],
      run: (key) => commit.run(key, {
        effects,
        write: async (commitKey) => {
          // The kept draft names the action this commit sent, so its result reads as that action.
          draft.set({ action, note: draft.value.note });
          try {
            const answer = offboarding
              ? await peopleApi.offboardMany(included.map((review) => ({ person_id: review.person_id, expected_updated_at: review.updated_at, fingerprint: review.fingerprint })), note, commitKey)
              : await peopleApi.withdrawMany(included.map((review) => ({ student_id: review.withdraw!.student_id, expected_updated_at: review.withdraw!.expected_updated_at })), note, commitKey);
            setSelection(new Map());
            return commit.confirmed(confirmedLines(answer));
          } catch (error) {
            // The refresh after a refused commit reads the selection again.
            if (error instanceof ApiError && error.status === 409) {
              return { state: "refused" as const, cap: offboarding ? "Nobody was offboarded." : "Nobody was withdrawn.", sentence: refusedSentence(error, "This did not go through.", offboarding ? nameOf : failedName), draft: "kept" as const };
            }
            throw error;
          }
        },
      }),
    },
    result: commit.result, draft: draft.kept || commit.pending,
  } as TaskBox<null> as unknown as Box;
}
